/**
 * shop-the-look-section.js
 * Handles:
 *  - Splide.js carousel initialization with custom dash pagination
 *  - Hotspot dot → product card highlight
 *  - Variant/size pill selection + real-time total price update
 *  - "Add All to Bag" multi-product cart addition
 *  - Shopify Theme Editor lifecycle (section reload, block select)
 */

(function () {
  'use strict';

  /* ── Money Formatting ───────────────────────────────────────── */
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.themeMoneyFormat || '₹{{amount_no_decimals}}');
    }
    // Fallback
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  /* ── Show / Hide global loader ─────────────────────────────── */
  function showLoader(id) {
    const el = id ? document.getElementById(id) : document.querySelector('.stl-loader-wrapper');
    if (el) el.classList.add('is-active');
  }
  function hideLoader(id) {
    const el = id ? document.getElementById(id) : document.querySelector('.stl-loader-wrapper');
    if (el) el.classList.remove('is-active');
  }

  /* ── Dispatch cart update events (opens drawer in most themes) */
  function notifyCartUpdated(cart) {
    // 1. Trigger the theme's CartDrawer.buildCart() which fetches cart HTML and shows items
    //    Theme listens on `document` for ajaxProduct:added → calls buildCart() → shows items
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      detail: { product: cart },
      bubbles: true
    }));
    // 2. Trigger cart rebuild (dev-friendly event the theme also listens to)
    document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
    // 3. Open the drawer — theme listens on `document` for cart:open
    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    // 4. Also fire on documentElement for other custom handlers
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', cart }
    }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    // 5. Update cart bubble / count immediately
    if (cart && cart.item_count !== undefined) {
      document.querySelectorAll('[data-cart-count],[data-cart-item-count],.cart-count,.cart-link__bubble-num').forEach(el => {
        el.textContent = cart.item_count;
      });
      document.querySelectorAll('.cart-link__bubble').forEach(el => {
        el.classList.toggle('cart-link__bubble--visible', cart.item_count > 0);
      });
    }
  }

  /* ── Add items to cart ─────────────────────────────────────── */
  async function addItemsToCart(items) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items })
    });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.description || data.message || 'Cart add failed');
      err.data = data;
      err.description = data.description || data.message;
      err.status = data.status || response.status;
      throw err;
    }
    return data;
  }

  async function addSingleItemToCart(id, quantity = 1) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id, quantity })
    });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.description || data.message || 'Item could not be added');
      err.data = data;
      err.description = data.description || data.message;
      err.status = data.status || response.status;
      throw err;
    }
    return data;
  }

  async function fetchCart() {
    const res = await fetch(`${Shopify.routes.root}cart.js`);
    return res.json();
  }

  /* ── ShopTheLookSection ─────────────────────────────────────── */
  class ShopTheLookSection {
    /**
     * @param {HTMLElement} sectionEl  – the <section> element
     */
    constructor(sectionEl) {
      if (!sectionEl) return;
      this.section    = sectionEl;
      this.sectionId  = sectionEl.dataset.sectionId || (sectionEl.id ? sectionEl.id.replace('stl-section-', '') : '');
      this.splideEl   = sectionEl.querySelector('.stl-splide');
      this.dashWrap   = sectionEl.querySelector('.stl-dash-pagination');
      this.loaderEl   = document.getElementById(`stl-loader-${this.sectionId}`);
      this.splide     = null;

      this._init();
    }

    /* ── Init ─────────────────────────────────────────────────── */
    _init() {
      this._initSplide();
      this._initVariantButtons();
      this._initDotButtons();
      this._initAtbButtons();
    }

    /* ── Splide ───────────────────────────────────────────────── */
    _initSplide() {
      if (!this.splideEl || typeof Splide === 'undefined') return;

      const autoScroll = this.splideEl.dataset.autoScroll === 'true';
      const autoScrollSpeed = (parseInt(this.splideEl.dataset.autoScrollSpeed, 10) || 5) * 1000;
      const slideCount = this.splideEl.querySelectorAll('.splide__slide').length;

      this.splide = new Splide(this.splideEl, {
        type:        'slide',
        rewind:      true,
        perPage:     1,
        perMove:     1,
        gap:         0,
        arrows:      false,
        pagination:  false,
        drag:        true,
        speed:       480,
        autoplay:    autoScroll && slideCount > 1,
        interval:    autoScrollSpeed,
        pauseOnHover: true,
        pauseOnFocus: true,
        easing:      'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        breakpoints: {}
      });

      // Sync custom dash pagination on slide move
      this.splide.on('moved active', (newIndex) => {
        const idx = typeof newIndex === 'number' ? newIndex : (this.splide ? this.splide.index : 0);
        this._updateDashes(idx);
      });

      this.splide.mount();

      // Pause autoplay when interacting with product cards
      if (autoScroll) {
        const productsCols = this.section.querySelectorAll('.stl-slide__products-col');
        productsCols.forEach(col => {
          col.addEventListener('mouseenter', () => {
            if (this.splide && this.splide.Components && this.splide.Components.Autoplay) {
              this.splide.Components.Autoplay.pause();
            }
          });
          col.addEventListener('mouseleave', () => {
            if (this.splide && this.splide.Components && this.splide.Components.Autoplay) {
              this.splide.Components.Autoplay.play();
            }
          });
        });
      }

      // Wire dash buttons to Splide
      if (this.dashWrap) {
        this.dashWrap.querySelectorAll('.stl-dash-pagination__dash').forEach((dash) => {
          dash.addEventListener('click', () => {
            const idx = parseInt(dash.dataset.dashIndex, 10);
            if (!isNaN(idx)) this.splide.go(idx);
          });
        });
      }
    }

    _updateDashes(activeIndex) {
      if (!this.dashWrap) return;
      this.dashWrap.querySelectorAll('.stl-dash-pagination__dash').forEach((dash, i) => {
        const isActive = i === activeIndex;
        dash.classList.toggle('is-active', isActive);
        dash.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    /* ── Option / Variant buttons ─────────────────────────────── */
    _initVariantButtons() {
      this.section.querySelectorAll('.stl-product-card').forEach(card => {
        card.querySelectorAll('.stl-variant-btn').forEach(btn => {
          // Sold-out pills are aria-disabled but we still wire the click so
          // we can give a visual no-op (the listener checks is-sold-out class).
          btn.addEventListener('click', () => {
            if (btn.classList.contains('is-sold-out')) return;
            this._onOptionSelect(card, btn);
          });
        });
      });
    }

    /**
     * Called when the user clicks a size/option pill.
     * Works for both single-option products (just variant-id approach)
     * AND multi-option products (Blazer Size + Trouser Size combos).
     */
    _onOptionSelect(card, btn) {
      const optionPosition = btn.dataset.optionPosition; // '1', '2', '3' or undefined (legacy)
      const optionCount    = parseInt(card.dataset.optionCount || '1', 10);

      if (optionPosition) {
        // ── Multi-option path ──────────────────────────────────
        // 1. Deselect siblings in the SAME option group only
        card.querySelectorAll(`.stl-variant-btn[data-option-position="${optionPosition}"]`).forEach(b => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');

        // 2. Collect currently selected value for each option position
        const selectedOptions = {};
        for (let pos = 1; pos <= optionCount; pos++) {
          const activeBtn = card.querySelector(`.stl-variant-btn[data-option-position="${pos}"].is-selected`);
          selectedOptions[pos] = activeBtn ? activeBtn.dataset.optionValue : null;
        }

        // 3. Find matching variant from embedded JSON
        const variantsData = this._getVariantsData(card);

        const matchingVariant = variantsData.find(v => {
          const o1Match = !selectedOptions[1] || v.o1 === selectedOptions[1];
          const o2Match = !selectedOptions[2] || v.o2 === selectedOptions[2];
          const o3Match = !selectedOptions[3] || v.o3 === selectedOptions[3];
          return o1Match && o2Match && o3Match && v.avail;
        }) || variantsData.find(v => {
          // Fallback: ignore availability to at least show a price
          const o1Match = !selectedOptions[1] || v.o1 === selectedOptions[1];
          const o2Match = !selectedOptions[2] || v.o2 === selectedOptions[2];
          const o3Match = !selectedOptions[3] || v.o3 === selectedOptions[3];
          return o1Match && o2Match && o3Match;
        });

        if (matchingVariant) {
          card.dataset.selectedVariantId = matchingVariant.id;
          card.dataset.selectedPrice     = matchingVariant.price;
          this._updateCardPrice(card, matchingVariant.price, matchingVariant.cp);
        }

        // 4. Update sold-out state of OTHER option groups based on new selection
        // Only run when variantsData loaded correctly — empty array would mark everything sold-out
        if (variantsData.length > 0) {
          this._refreshSoldOutStates(card, variantsData, selectedOptions, parseInt(optionPosition, 10));
        }

      } else {
        // ── Legacy single-variant path (data-variant-id on each btn) ──
        card.querySelectorAll('.stl-variant-btn').forEach(b => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        card.dataset.selectedVariantId = btn.dataset.variantId;
        card.dataset.selectedPrice     = btn.dataset.variantPrice;
        this._updateCardPrice(card, parseInt(btn.dataset.variantPrice, 10), parseInt(btn.dataset.variantComparePrice || '0', 10));
      }

      // Clear any previous stock error state on this card
      card.classList.remove('has-stock-error');
      const prevError = card.querySelector('.stl-card-error-msg');
      if (prevError) prevError.remove();

      // Clear any feedback banner below the ATC button on this slide
      const slideIdx = card.closest('[data-stl-products-list]')?.dataset.slideIndex;
      if (slideIdx !== undefined) {
        const atbWrap = this.section.querySelector(`[data-stl-atb][data-slide-index="${slideIdx}"]`)?.closest('.stl-atb-wrapper');
        if (atbWrap) {
          const fb = atbWrap.querySelector('.stl-atb-feedback');
          if (fb) fb.remove();
        }
      }

      this._recalcSlideTotal(card);
    }

    /** Update price + compare-price text on the card */
    _updateCardPrice(card, priceCents, comparePriceCents) {
      const priceEl       = card.querySelector('[data-price-display]');
      const compareEl     = card.querySelector('[data-compare-price-display]');
      if (priceEl) priceEl.textContent = formatMoney(priceCents);
      if (compareEl) {
        if (comparePriceCents && comparePriceCents > priceCents) {
          compareEl.textContent   = formatMoney(comparePriceCents);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    }

    /** Read variants JSON from the embedded <script class="stl-variants-json"> tag */
    _getVariantsData(card) {
      const scriptEl = card.querySelector('.stl-variants-json');
      if (!scriptEl) return [];
      try {
        return JSON.parse(scriptEl.textContent || '[]');
      } catch (e) {
        console.warn('[STL] Could not parse variants JSON', e);
        return [];
      }
    }

    /**
     * After selecting an option, update sold-out styling on OTHER option groups.
     * E.g. after picking "Blazer Size: XXS", mark Trouser sizes with no available
     * XXS-Blazer variant as sold out.
     */
    _refreshSoldOutStates(card, variantsData, selectedOptions, changedPosition) {
      const optionCount = parseInt(card.dataset.optionCount || '1', 10);

      for (let pos = 1; pos <= optionCount; pos++) {
        if (pos === changedPosition) continue; // skip the row the user just clicked

        card.querySelectorAll(`.stl-variant-btn[data-option-position="${pos}"]`).forEach(btn => {
          const value = btn.dataset.optionValue;

          // Check if any available variant has the current selections + this value
          const isAvailable = variantsData.some(v => {
            const constraints = { ...selectedOptions, [pos]: value };
            const o1OK = !constraints[1] || v.o1 === constraints[1];
            const o2OK = !constraints[2] || v.o2 === constraints[2];
            const o3OK = !constraints[3] || v.o3 === constraints[3];
            return o1OK && o2OK && o3OK && v.avail;
          });

          btn.classList.toggle('is-sold-out', !isAvailable);
        });
      }
    }


    _recalcSlideTotal(changedCard) {
      const list = changedCard.closest('[data-stl-products-list]');
      if (!list) return;
      const slideIndex = list.dataset.slideIndex;

      let total = 0;
      list.querySelectorAll('.stl-product-card').forEach(c => {
        total += parseInt(c.dataset.selectedPrice || '0', 10);
      });

      // Update ATB button price
      const atbBtn = this.section.querySelector(`[data-stl-atb][data-slide-index="${slideIndex}"]`);
      if (atbBtn) {
        const priceSpan = atbBtn.querySelector('[data-stl-total-price]');
        if (priceSpan) priceSpan.textContent = formatMoney(total);
        atbBtn.setAttribute('aria-label',
          `${atbBtn.querySelector('.stl-atb-btn__text')?.textContent || 'Add All to Bag'} — ${formatMoney(total)}`
        );
      }
    }

    /* ── Hotspot dot → card highlight ────────────────────────── */
    _initDotButtons() {
      this.section.querySelectorAll('.stl-dot__btn').forEach(dot => {
        dot.addEventListener('click', () => {
          const targetId = dot.dataset.dotTarget;
          if (!targetId) return;
          const card = this.section.getElementById
            ? null
            : document.getElementById(targetId);
          const cardEl = document.getElementById(targetId);
          if (!cardEl) return;

          // Remove highlight from siblings
          const list = cardEl.closest('[data-stl-products-list]');
          if (list) {
            list.querySelectorAll('.stl-product-card').forEach(c => c.classList.remove('is-highlighted'));
          }
          cardEl.classList.add('is-highlighted');
          cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

          // Auto-remove highlight after 2s
          clearTimeout(cardEl._highlightTimer);
          cardEl._highlightTimer = setTimeout(() => cardEl.classList.remove('is-highlighted'), 2000);
        });

        // Also highlight on hover (desktop)
        dot.addEventListener('mouseenter', () => {
          const cardEl = document.getElementById(dot.dataset.dotTarget);
          if (cardEl) cardEl.classList.add('is-highlighted');
        });
        dot.addEventListener('mouseleave', () => {
          const cardEl = document.getElementById(dot.dataset.dotTarget);
          if (cardEl) cardEl.classList.remove('is-highlighted');
        });
      });
    }

    /* ── Add All to Bag ───────────────────────────────────────── */
    _initAtbButtons() {
      this.section.querySelectorAll('[data-stl-atb]').forEach(btn => {
        btn.addEventListener('click', () => this._onAtbClick(btn));
      });
    }

    _showAtbFeedback(btn, type, message) {
      const atbWrap = btn.closest('.stl-atb-wrapper');
      if (!atbWrap) return;
      const existing = atbWrap.querySelector('.stl-atb-feedback');
      if (existing) existing.remove();

      let iconSvg = '';
      if (type === 'warning') {
        iconSvg = '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>';
      } else if (type === 'info') {
        iconSvg = '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>';
      } else {
        iconSvg = '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>';
      }

      const fb = document.createElement('div');
      fb.className = `stl-atb-feedback stl-atb-feedback--${type}`;
      fb.innerHTML = `${iconSvg}<span>${message}</span>`;
      atbWrap.appendChild(fb);
    }

    _markCardStockError(card, message) {
      card.classList.add('has-stock-error');
      const infoEl = card.querySelector('.stl-product-card__info') || card;
      const existing = card.querySelector('.stl-card-error-msg');
      if (existing) existing.remove();

      const msgEl = document.createElement('div');
      msgEl.className = 'stl-card-error-msg';
      msgEl.innerHTML = `
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <span>${message || 'Maximum available quantity already in cart'}</span>
      `;
      infoEl.appendChild(msgEl);
    }

    async _onAtbClick(btn) {
      const slideIndex = btn.dataset.slideIndex;
      const list = this.section.querySelector(`[data-stl-products-list][data-slide-index="${slideIndex}"]`);
      if (!list) return;

      // Clear previous error states on this slide
      list.querySelectorAll('.stl-product-card').forEach(c => {
        c.classList.remove('has-stock-error');
        const pe = c.querySelector('.stl-card-error-msg');
        if (pe) pe.remove();
      });
      const atbWrap = btn.closest('.stl-atb-wrapper');
      if (atbWrap) {
        const existingFb = atbWrap.querySelector('.stl-atb-feedback');
        if (existingFb) existingFb.remove();
      }

      // Gather selected variants with card references
      const itemCards = [];
      list.querySelectorAll('.stl-product-card').forEach(card => {
        const vid = card.dataset.selectedVariantId;
        if (vid) {
          const titleEl = card.querySelector('.stl-product-card__title');
          itemCards.push({
            card,
            id: parseInt(vid, 10),
            quantity: 1,
            title: titleEl ? titleEl.textContent.trim() : 'Item'
          });
        }
      });

      if (itemCards.length === 0) return;

      // Disable button + show loader
      btn.disabled = true;
      const originalHtml = btn.innerHTML;
      btn.innerHTML = `<span class="stl-atb-btn__text" style="letter-spacing:.1em">Adding…</span>`;
      if (this.loaderEl) this.loaderEl.classList.add('is-active');

      const items = itemCards.map(ic => ({ id: ic.id, quantity: ic.quantity }));
      let addedItems = [];
      let failedItems = [];

      try {
        // Attempt fast batch add first
        await addItemsToCart(items);
        addedItems = itemCards;
      } catch (batchErr) {
        console.warn('[ShopTheLook] Batch add failed, attempting individual addition:', batchErr);
        // Batch failed (likely 422 stock error on one or more items).
        // Try adding items individually so available items are still added!
        for (const ic of itemCards) {
          try {
            await addSingleItemToCart(ic.id, ic.quantity);
            addedItems.push(ic);
          } catch (itemErr) {
            console.warn(`[ShopTheLook] Could not add "${ic.title}":`, itemErr);
            const rawMsg = itemErr.description || itemErr.message || 'Maximum quantity already in cart';
            failedItems.push({
              card: ic.card,
              title: ic.title,
              errorMsg: rawMsg
            });
          }
        }
      } finally {
        if (this.loaderEl) this.loaderEl.classList.remove('is-active');
      }

      // Always update cart & drawer if any item was added or if items were already in cart
      if (addedItems.length > 0 || failedItems.length > 0) {
        try {
          const cart = await fetchCart();
          notifyCartUpdated(cart);
          if (window.CartStore) window.CartStore.set(cart);
        } catch (e) {
          console.warn('[ShopTheLook] Failed to refresh cart:', e);
        }
      }

      // Mark failed product cards
      failedItems.forEach(item => {
        this._markCardStockError(item.card, item.errorMsg);
      });

      // Handle outcome states:
      if (failedItems.length === 0 && addedItems.length > 0) {
        // 1. Full Success
        btn.innerHTML = `<span class="stl-atb-btn__text">✓ Added to Bag</span>`;
        setTimeout(() => {
          btn.innerHTML = originalHtml;
          btn.disabled = false;
        }, 2000);

      } else if (addedItems.length > 0 && failedItems.length > 0) {
        // 2. Partial Success (some added, some hit limit)
        const failedNames = failedItems.map(f => `"${f.title}"`).join(', ');
        btn.innerHTML = `<span class="stl-atb-btn__text">✓ Added (${failedItems.length} at Max Limit)</span>`;
        this._showAtbFeedback(
          btn,
          'warning',
          `${addedItems.length} item${addedItems.length > 1 ? 's' : ''} added to your bag. ${failedNames} already reached maximum available quantity.`
        );
        setTimeout(() => {
          btn.innerHTML = originalHtml;
          btn.disabled = false;
        }, 3500);

      } else if (failedItems.length > 0 && addedItems.length === 0) {
        // 3. All items already reached max limit in cart
        btn.innerHTML = `<span class="stl-atb-btn__text">Already in Bag (Max Limit)</span>`;
        this._showAtbFeedback(
          btn,
          'info',
          `The selected item${failedItems.length > 1 ? 's are' : ' is'} already in your bag with the maximum available quantity.`
        );
        setTimeout(() => {
          btn.innerHTML = originalHtml;
          btn.disabled = false;
        }, 3500);

      } else {
        // 4. Unexpected error / network issue
        btn.innerHTML = `<span class="stl-atb-btn__text">Could Not Add</span>`;
        this._showAtbFeedback(
          btn,
          'error',
          'Unable to add items right now. Please check your connection and try again.'
        );
        setTimeout(() => {
          btn.innerHTML = originalHtml;
          btn.disabled = false;
        }, 3000);
      }
    }

    /* ── Cleanup ──────────────────────────────────────────────── */
    destroy() {
      if (this.splide) {
        this.splide.destroy();
        this.splide = null;
      }
    }
  }

  /* ── Instance registry ──────────────────────────────────────── */
  const instances = new Map();

  function initSection(container) {
    if (!container) return;
    const sectionEl = container.querySelector('.stl-section') || container;

    // Avoid double-init
    if (instances.has(sectionEl.id)) {
      instances.get(sectionEl.id).destroy();
    }

    // Wait for Splide to be available
    if (typeof Splide === 'undefined') {
      const splideScript = document.querySelector('script[src*="splide.min.js"]');
      if (splideScript) {
        splideScript.addEventListener('load', () => {
          const inst = new ShopTheLookSection(sectionEl);
          if (sectionEl.id) instances.set(sectionEl.id, inst);
        }, { once: true });
      }
    } else {
      const inst = new ShopTheLookSection(sectionEl);
      if (sectionEl.id) instances.set(sectionEl.id, inst);
    }
  }

  function initAll() {
    document.querySelectorAll('.stl-section').forEach(el => initSection(el.parentElement || el));
  }

  /* ── Boot ───────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    // DOM already ready (deferred script)
    initAll();
  }

  /* ── Shopify Theme Editor ───────────────────────────────────── */
  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl = e.target.querySelector('.stl-section');
    if (sectionEl) {
      // Small timeout to ensure assets are loaded
      setTimeout(() => initSection(e.target), 150);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sectionEl = e.target.querySelector('.stl-section');
    if (sectionEl && instances.has(sectionEl.id)) {
      instances.get(sectionEl.id).destroy();
      instances.delete(sectionEl.id);
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const sectionEl = e.target.closest('.stl-section');
    if (!sectionEl) return;
    const instance = instances.get(sectionEl.id);
    if (!instance || !instance.splide) return;
    const slide = e.target.closest('.splide__slide');
    if (!slide) return;
    const idx = [...slide.parentElement.children].indexOf(slide);
    if (idx >= 0) instance.splide.go(idx);
  });

  /* ── Hash Scroll Handler ────────────────────────────────────── */
  function handleHashScroll() {
    if (!window.location.hash) return;
    const hash = window.location.hash;
    const target = document.querySelector(hash);
    if (target && (target.classList.contains('stl-anchor-target') || target.classList.contains('stl-section') || target.closest('.stl-section'))) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleHashScroll);
  } else {
    handleHashScroll();
  }
  window.addEventListener('load', handleHashScroll);

})();
